import React, { useState } from 'react';

interface GraphControlsProps {
  onApplyForceLayout: () => void;
  onSaveLayout: () => void;
  onFitView: () => void;
  onCreateGroup?: () => void;
  onExpandNode?: (nodeId: string, nodeType: string) => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onToggleMinimap?: () => void;
}

export const GraphControls: React.FC<GraphControlsProps> = ({
  onApplyForceLayout,
  onSaveLayout,
  onFitView,
  onCreateGroup,
  onExpandNode,
  onZoomIn,
  onZoomOut,
  onToggleMinimap,
}) => {
  const [expandDialogOpen, setExpandDialogOpen] = useState(false);
  const [nodeId, setNodeId] = useState('');
  const [nodeType, setNodeType] = useState('');

  return (
    <>
      <div className="graph-controls absolute top-4 right-4 bg-white rounded-lg shadow-lg p-2 z-10">
        <div className="flex flex-col space-y-2">
          <button
            className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
            onClick={onApplyForceLayout}
            title="Apply force-directed layout"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 00-1 1v4H5a1 1 0 100 2h4v4a1 1 0 102 0v-4h4a1 1 0 100-2h-4V4a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </button>
          
          <button
            className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
            onClick={onFitView}
            title="Fit view"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          </button>
          
          <button
            className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
            onClick={onSaveLayout}
            title="Save layout"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
          
          {/* New Group button */}
          {onCreateGroup && (
            <button
              className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
              onClick={onCreateGroup}
              title="Group selected nodes"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
              </svg>
            </button>
          )}
          
          {/* Expand Node button */}
          {onExpandNode && (
            <button
              className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
              onClick={() => setExpandDialogOpen(true)}
              title="Expand node"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          
          {onZoomIn && (
            <button
              className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
              onClick={onZoomIn}
              title="Zoom in"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          
          {onZoomOut && (
            <button
              className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
              onClick={onZoomOut}
              title="Zoom out"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          
          {onToggleMinimap && (
            <button
              className="p-2 hover:bg-gray-100 rounded flex items-center justify-center"
              onClick={onToggleMinimap}
              title="Toggle minimap"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm11 1H6v8l4-2 4 2V6z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      {/* Expand Node Dialog */}
      {expandDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-medium mb-4">Expand Node</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Node ID
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={nodeId}
                onChange={(e) => setNodeId(e.target.value)}
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Node Type
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={nodeType}
                onChange={(e) => setNodeType(e.target.value)}
              >
                <option value="">Select a type</option>
                <option value="session">Session</option>
                <option value="agent">Agent</option>
                <option value="artifact">Artifact</option>
                <option value="phase">Phase</option>
              </select>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setExpandDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={() => {
                  if (nodeId && nodeType && onExpandNode) {
                    onExpandNode(nodeId, nodeType);
                    setExpandDialogOpen(false);
                  }
                }}
                disabled={!nodeId || !nodeType}
              >
                Expand
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
